export interface ContextualFatigueInput {
  frequency?: number;
  qualifiedTrafficChange?: number;
  realizedValueChange?: number;
  projectedValueChange?: number;
  contributionPltvChange?: number;
  confidenceLow?: number;
  minimumMaterialDecline: number;
  placementAdjusted: boolean;
  audienceMixAdjusted: boolean;
  spendReallocationAdjusted: boolean;
  seasonalityAdjusted: boolean;
  auctionCostAdjusted: boolean;
  spacingAdjusted: boolean;
  registeredChangePointPolicyRef?: string;
  changePointInferenceMethod?: 'bonferroni_simultaneous' | 'honest_split' | 'unadjusted_search';
  changePointInferencePassed: boolean;
  lineageState: 'valid' | 'missing' | 'stale';
  fatigueClockRule: 'inherit_history' | 'reset_after_qualified_refresh';
  relaunchState: 'none' | 'same_artifact_relaunch' | 'refreshed_artifact_relaunch';
  priorEvidenceRef?: string;
}

export function evaluateContextualFatigue(input: ContextualFatigueInput): {
  state: 'stable' | 'fatiguing' | 'inconclusive';
  reasonCodes: string[];
} {
  const controlsComplete = input.placementAdjusted
    && input.audienceMixAdjusted
    && input.spendReallocationAdjusted
    && input.seasonalityAdjusted
    && input.auctionCostAdjusted
    && input.spacingAdjusted;
  if (!controlsComplete) return { state: 'inconclusive', reasonCodes: ['context_controls_incomplete'] };
  if (!input.registeredChangePointPolicyRef) {
    return { state: 'inconclusive', reasonCodes: ['change_point_registration_missing'] };
  }
  if (!['bonferroni_simultaneous', 'honest_split'].includes(String(input.changePointInferenceMethod))) {
    return { state: 'inconclusive', reasonCodes: ['change_point_inference_unadjusted'] };
  }
  if (input.lineageState !== 'valid') {
    return { state: 'inconclusive', reasonCodes: [`fatigue_lineage_${input.lineageState}`] };
  }
  const relaunch = input.relaunchState !== 'none';
  if ((relaunch || input.fatigueClockRule === 'reset_after_qualified_refresh') && !input.priorEvidenceRef) {
    return { state: 'inconclusive', reasonCodes: ['fatigue_prior_lineage_required'] };
  }
  if (input.fatigueClockRule === 'reset_after_qualified_refresh'
    && input.relaunchState !== 'refreshed_artifact_relaunch') {
    return { state: 'inconclusive', reasonCodes: ['fatigue_clock_reset_not_qualified'] };
  }
  const qualifiedDecline = finite(input.qualifiedTrafficChange)
    && (input.qualifiedTrafficChange as number) <= -Math.abs(input.minimumMaterialDecline);
  const valueDecline = [
    input.realizedValueChange,
    input.projectedValueChange,
    input.contributionPltvChange,
    input.confidenceLow,
  ]
    .filter(finite)
    .some((value) => value <= -Math.abs(input.minimumMaterialDecline));
  if (input.changePointInferencePassed && (qualifiedDecline || valueDecline)) {
    return {
      state: 'fatiguing',
      reasonCodes: [
        'selection_adjusted_change_point_supported',
        ...(qualifiedDecline ? ['qualified_traffic_declined'] : []),
        ...(valueDecline ? ['value_declined'] : []),
      ],
    };
  }
  // Frequency is retained as context only. There is intentionally no fixed
  // threshold and it cannot produce fatigue without value/quality evidence.
  return {
    state: input.changePointInferencePassed ? 'inconclusive' : 'stable',
    reasonCodes: input.changePointInferencePassed ? ['change_point_without_material_value_decline'] : [],
  };
}

export type ConsumerHarmMetric =
  | 'refund_rate'
  | 'dispute_rate'
  | 'complaint_rate'
  | 'cancel_effort'
  | 'hidden_recurring_terms'
  | 'trust_indicator';

export interface ConsumerHarmGuardrail {
  metric: ConsumerHarmMetric;
  observed: number;
  maximumAllowed: number;
  evidenceRef: string;
}

export function evaluateConsumerHarmGuardrails(guardrails: ConsumerHarmGuardrail[]): {
  passed: boolean;
  vetoScale: boolean;
  breaches: ConsumerHarmGuardrail[];
} {
  const breaches = guardrails.filter((guardrail) => (
    Number.isFinite(guardrail.observed)
      && Number.isFinite(guardrail.maximumAllowed)
      && guardrail.observed > guardrail.maximumAllowed
  ));
  return { passed: breaches.length === 0, vetoScale: breaches.length > 0, breaches };
}

export const CANONICAL_DARK_PATTERN_KINDS = [
  'fake_scarcity_or_countdown',
  'hidden_recurring_terms',
  'preselection',
  'false_hierarchy',
  'confirmshaming',
  'forced_collection',
  'asymmetric_cancel',
  'undisclosed_fees',
  'disguised_ad_or_testimonial',
  'misleading_social_proof',
] as const;

export type DarkPatternKind = typeof CANONICAL_DARK_PATTERN_KINDS[number];
export type LegacyDarkPatternKind =
  | 'fake_scarcity'
  | 'hidden_subscription_terms'
  | 'preselected_purchase'
  | 'obstructed_cancel'
  | 'simulated_processing'
  | 'forced_continuity';

const LEGACY_DARK_PATTERN_ALIASES: Record<LegacyDarkPatternKind, DarkPatternKind> = {
  fake_scarcity: 'fake_scarcity_or_countdown',
  hidden_subscription_terms: 'hidden_recurring_terms',
  preselected_purchase: 'preselection',
  obstructed_cancel: 'asymmetric_cancel',
  simulated_processing: 'false_hierarchy',
  forced_continuity: 'hidden_recurring_terms',
};

const LEGAL_DISCLAIMER = 'This automated check does not assert legal compliance.' as const;

export interface DarkPatternObservation {
  pattern: DarkPatternKind | LegacyDarkPatternKind;
  evidenceLocation: string;
  severity: 'warning' | 'critical' | 'blocker';
  observed: boolean;
}

export function evaluateDarkPatterns(observations: DarkPatternObservation[]): {
  status: 'review_required' | 'no_observed_patterns';
  findings: Array<{
    pattern: DarkPatternKind;
    evidenceLocation: string;
    severity: 'warning' | 'blocker';
    requiredReview: 'legal_and_product_review';
    legalDisclaimer: typeof LEGAL_DISCLAIMER;
    legacyAlias: LegacyDarkPatternKind | null;
  }>;
  winnerBlocked: boolean;
  disclaimer: typeof LEGAL_DISCLAIMER;
} {
  const findings = observations
    .filter((observation) => observation.observed)
    .map((observation) => {
      const pattern = normalizeDarkPatternKind(observation.pattern);
      return {
        pattern,
        evidenceLocation: observation.evidenceLocation,
        severity: observation.severity === 'warning' ? 'warning' as const : 'blocker' as const,
        requiredReview: 'legal_and_product_review' as const,
        legalDisclaimer: LEGAL_DISCLAIMER,
        legacyAlias: pattern === observation.pattern ? null : observation.pattern as LegacyDarkPatternKind,
      };
    });
  return {
    status: findings.length > 0 ? 'review_required' : 'no_observed_patterns',
    findings,
    winnerBlocked: findings.some(({ severity }) => severity === 'blocker'),
    disclaimer: LEGAL_DISCLAIMER,
  };
}

export function normalizeDarkPatternKind(pattern: DarkPatternKind | LegacyDarkPatternKind): DarkPatternKind {
  if ((CANONICAL_DARK_PATTERN_KINDS as readonly string[]).includes(pattern)) return pattern as DarkPatternKind;
  const normalized = LEGACY_DARK_PATTERN_ALIASES[pattern as LegacyDarkPatternKind];
  if (!normalized) throw new Error(`Unsupported dark-pattern category: ${String(pattern)}`);
  return normalized;
}

export function evaluateEthicalWinnerDecision(input: {
  conversionImproved: boolean;
  guardrails: ConsumerHarmGuardrail[];
  darkPatterns: DarkPatternObservation[];
}): {
  winnerEligible: boolean;
  vetoScale: boolean;
  reasonCodes: string[];
} {
  const harm = evaluateConsumerHarmGuardrails(input.guardrails);
  const darkPatterns = evaluateDarkPatterns(input.darkPatterns);
  const reasonCodes = [
    ...(!input.conversionImproved ? ['conversion_improvement_not_supported'] : []),
    ...harm.breaches.map(({ metric }) => `consumer_harm_${metric}_breached`),
    ...darkPatterns.findings
      .filter(({ severity }) => severity === 'blocker')
      .map(({ pattern }) => `dark_pattern_${pattern}`),
  ];
  const vetoScale = harm.vetoScale || darkPatterns.winnerBlocked;
  return { winnerEligible: input.conversionImproved && !vetoScale, vetoScale, reasonCodes };
}

export type FrictionClass = 'accidental' | 'trust' | 'qualification' | 'protective' | 'dark_sludge';

export const REQUIRED_FRICTION_OUTCOMES = [
  'activation',
  'paid_conversion',
  'renewal',
  'refund',
  'chargeback',
  'contribution_value',
] as const;

export type FrictionOutcomeMetricKey = typeof REQUIRED_FRICTION_OUTCOMES[number];

export interface FrictionOutcomeRegistration {
  metricKey: FrictionOutcomeMetricKey;
  metricRef: string;
  metricHash: string;
  maturityDays: number;
}

export interface FrictionExperimentRegistration {
  frictionClass: FrictionClass;
  registrationRef: string;
  registrationVersion: string;
  registrationHash: string;
  experimentRegistryRef: string;
  experimentRegistryHash: string;
  outcomeRegistrations: FrictionOutcomeRegistration[];
}

export interface FrictionOutcomeEvidence {
  metricKey: FrictionOutcomeMetricKey;
  metricRef: string;
  resultRef: string;
  resultHash: string;
  observedDays: number;
  maturityState: 'mature' | 'immature';
  integrityGateState: 'passed' | 'failed';
}

export function validateFrictionExperiment(
  registration: FrictionExperimentRegistration,
  evidence: FrictionOutcomeEvidence[] = [],
): {
  valid: boolean;
  decisionEligible: boolean;
  missingOutcomeMetricKeys: FrictionOutcomeMetricKey[];
  invalidRegistrationMetricKeys: FrictionOutcomeMetricKey[];
  missingEvidenceMetricKeys: FrictionOutcomeMetricKey[];
  immatureMetricKeys: FrictionOutcomeMetricKey[];
  failedIntegrityMetricKeys: FrictionOutcomeMetricKey[];
} {
  const registered = new Map(registration.outcomeRegistrations.map((outcome) => [outcome.metricKey, outcome]));
  const missingOutcomeMetricKeys = REQUIRED_FRICTION_OUTCOMES.filter((key) => !registered.has(key));
  const duplicateRegistration = registered.size !== registration.outcomeRegistrations.length;
  const identityValid = opaqueRef(registration.registrationRef)
    && boundedVersion(registration.registrationVersion)
    && sha256(registration.registrationHash)
    && opaqueRef(registration.experimentRegistryRef)
    && sha256(registration.experimentRegistryHash);
  const invalidRegistrationMetricKeys = registration.outcomeRegistrations
    .filter((outcome) => !opaqueRef(outcome.metricRef)
      || !sha256(outcome.metricHash)
      || !Number.isSafeInteger(outcome.maturityDays)
      || outcome.maturityDays < 0)
    .map(({ metricKey }) => metricKey);
  const valid = identityValid && !duplicateRegistration
    && missingOutcomeMetricKeys.length === 0 && invalidRegistrationMetricKeys.length === 0;
  const evidenceByMetric = new Map(evidence.map((row) => [row.metricKey, row]));
  const missingEvidenceMetricKeys = valid
    ? REQUIRED_FRICTION_OUTCOMES.filter((key) => !evidenceByMetric.has(key))
    : [...REQUIRED_FRICTION_OUTCOMES];
  const immatureMetricKeys: FrictionOutcomeMetricKey[] = [];
  const failedIntegrityMetricKeys: FrictionOutcomeMetricKey[] = [];
  if (valid) {
    for (const metricKey of REQUIRED_FRICTION_OUTCOMES) {
      const outcome = registered.get(metricKey)!;
      const result = evidenceByMetric.get(metricKey);
      if (!result) continue;
      const exactBinding = result.metricRef === outcome.metricRef
        && opaqueRef(result.resultRef) && sha256(result.resultHash);
      if (!exactBinding || result.integrityGateState !== 'passed') failedIntegrityMetricKeys.push(metricKey);
      if (result.maturityState !== 'mature'
        || !Number.isSafeInteger(result.observedDays)
        || result.observedDays < outcome.maturityDays) immatureMetricKeys.push(metricKey);
    }
  }
  const decisionEligible = valid
    && missingEvidenceMetricKeys.length === 0
    && immatureMetricKeys.length === 0
    && failedIntegrityMetricKeys.length === 0;
  return {
    valid,
    decisionEligible,
    missingOutcomeMetricKeys,
    invalidRegistrationMetricKeys,
    missingEvidenceMetricKeys,
    immatureMetricKeys,
    failedIntegrityMetricKeys,
  };
}

export interface LearningSnapshotInput {
  snapshotId: string;
  sourceQueryVersion: string;
  evidenceRefs: string[];
  normalizedFindings: Array<{ key: string; value: string | number | boolean | null }>;
  uncertainty: string;
  expiresAt: string;
  mutableMetricRows?: unknown[];
}

export interface LearningSnapshot {
  snapshotId: string;
  sourceQueryVersion: string;
  evidenceRefs: string[];
  normalizedFindings: Array<{ key: string; value: string | number | boolean | null }>;
  uncertainty: string;
  expiresAt: string;
  authority: 'provenance_bound_learning_not_metric_truth';
}

export function buildLearningSnapshot(input: LearningSnapshotInput): LearningSnapshot {
  if (!input.sourceQueryVersion
    || input.evidenceRefs.length === 0
    || !Number.isFinite(Date.parse(input.expiresAt))) {
    throw new Error('learning snapshot requires source version, evidence, and a valid expiry');
  }
  if (input.normalizedFindings.length > 50) throw new Error('learning snapshot findings exceed the bounded contract');
  const sensitive = /password|token|secret|email|credential|free[_ -]?text/i;
  if (input.normalizedFindings.some((finding) => sensitive.test(finding.key))) {
    throw new Error('sensitive or free-text fields are prohibited in learning snapshots');
  }
  return {
    snapshotId: input.snapshotId,
    sourceQueryVersion: input.sourceQueryVersion,
    evidenceRefs: [...new Set(input.evidenceRefs)].sort(),
    normalizedFindings: input.normalizedFindings.map((finding) => ({ ...finding })),
    uncertainty: input.uncertainty,
    expiresAt: input.expiresAt,
    authority: 'provenance_bound_learning_not_metric_truth',
  };
}

function finite(value: number | undefined): value is number {
  return value !== undefined && Number.isFinite(value);
}

function opaqueRef(value: unknown): value is string {
  return typeof value === 'string'
    && /^[A-Za-z][A-Za-z0-9._-]{0,63}:[A-Za-z0-9][A-Za-z0-9._:-]{0,447}$/.test(value)
    && !value.includes('://') && !value.includes('..') && !value.includes('Bearer');
}

function sha256(value: unknown): value is string {
  return typeof value === 'string' && /^(?:sha256:)?[a-f0-9]{64}$/i.test(value);
}

function boundedVersion(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9._:-]{0,255}$/.test(value);
}
